/*
 * Cart page progressive enhancement.
 *
 * The section ships as a plain form post to /cart: quantities are number
 * inputs and there is an Update button to submit them. That works with
 * scripts off, which is the same rule the rest of this theme follows.
 *
 * With JavaScript available:
 *   - the minus and plus buttons are revealed and the Update button is hidden,
 *     from here rather than in the markup, so a page with no JS never shows a
 *     control that does nothing or loses the only way to change a quantity
 *   - a change is sent to /cart/change.js and the section is re-rendered in
 *     place from the Section Rendering API, which rides back on the same
 *     response. Liquid still formats every price; nothing is formatted here.
 *
 * It used to call form.submit() on every change. Measured live that was about
 * 2.6 seconds with no sign of life, then a full reload that threw the page
 * back to the top. Now the figures being replaced dim at once and the page
 * keeps its place.
 *
 * If the request fails for any reason, it falls back to exactly that native
 * post, so the worst case is the old behaviour rather than a dead control.
 */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // The header's cart count. The header renders no number at all for an empty
  // cart, so it may have to be made. The same few lines live in
  // peanut-cart-sheet.js, which is the other place a count changes in place.
  function setCount(count) {
    var link = document.querySelector('.peanut-nav__cart');
    if (!link) return;
    var badge = link.querySelector('[data-peanut-cart-count]');
    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'peanut-nav__cart-count';
        badge.setAttribute('aria-hidden', 'true');
        badge.setAttribute('data-peanut-cart-count', '');
        link.appendChild(badge);
      }
      badge.textContent = count > 99 ? '99+' : String(count);
    } else if (badge) {
      badge.remove();
    }
    link.setAttribute(
      'aria-label',
      count === 0 ? 'Cart, empty' : count === 1 ? 'Cart, 1 item' : 'Cart, ' + count + ' items'
    );
  }

  function init(root) {
    var form = root.querySelector('form.peanut-cart__form');
    if (!form) return;

    var sectionId = root.getAttribute('data-section-id');
    var update = form.querySelector('[data-peanut-cart-update]');
    if (update) update.hidden = true;

    root.classList.add('peanut-cart--enhanced');

    // The old path, kept as the fallback. submit() rather than requestSubmit():
    // it never carries a submitter, so the post has neither 'update' nor
    // 'checkout' on it. Shopify applies the updates[] values and returns to
    // /cart. requestSubmit() could pick up the checkout button and send the
    // visitor to the payment page instead.
    function nativePost() {
      form.submit();
    }

    function busy() {
      return root.getAttribute('aria-busy') === 'true';
    }

    // fallback is what to do if the request cannot be made or fails. For a
    // quantity that is the native post, which carries the field's new value.
    // Remove passes its own, because the form has no field that says remove.
    function change(row, quantity, focusHint, fallback) {
      var key = row.getAttribute('data-peanut-line');
      fallback = fallback || nativePost;
      if (!key || !sectionId || !window.fetch) return fallback();
      if (busy()) return;

      root.setAttribute('aria-busy', 'true');
      row.setAttribute('data-pending', '');

      fetch(ROOT + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: [sectionId],
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('cart change refused');
          return response.json();
        })
        .then(function (cart) {
          var html = cart.sections && cart.sections[sectionId];
          if (!html) throw new Error('no section in response');

          var next = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-peanut-cart]');
          if (!next) throw new Error('section did not parse');

          // The arrival animation is for arriving. This is an edit.
          next.classList.add('peanut-cart--rerendered');
          root.replaceWith(next);
          init(next);
          setCount(cart.item_count);

          // Put the keyboard back where it was. The node that had focus has
          // been replaced, so it is found again by line and by direction.
          if (focusHint) {
            var sameRow = next.querySelector('[data-peanut-line="' + key + '"]');
            var target = sameRow && sameRow.querySelector(focusHint);
            if (target && !target.disabled) target.focus();
            else if (sameRow && sameRow.querySelector('[data-peanut-qty]')) sameRow.querySelector('[data-peanut-qty]').focus();
            else {
              var heading = next.querySelector('h1');
              if (heading) {
                heading.setAttribute('tabindex', '-1');
                heading.focus();
              }
            }
          }
        })
        .catch(fallback);
    }

    form.querySelectorAll('[data-peanut-line]').forEach(function (row) {
      var input = row.querySelector('[data-peanut-qty]');
      if (!input) return;

      var max = parseInt(input.getAttribute('max'), 10);
      if (!(max > 0)) max = Infinity;
      var previous = input.value;

      var minus = row.querySelector('[data-peanut-step="-1"]');
      var plus = row.querySelector('[data-peanut-step="1"]');

      [minus, plus].forEach(function (button) {
        if (!button) return;
        button.hidden = false;
        var step = parseInt(button.getAttribute('data-peanut-step'), 10);

        button.addEventListener('click', function () {
          // One change at a time. A second press while the first is in flight
          // would move the number on screen without sending it.
          if (busy()) return;
          var current = parseInt(input.value, 10) || 0;
          var wanted = Math.max(0, Math.min(max, current + step));
          if (wanted === current) return;
          input.value = wanted;
          previous = String(wanted);
          change(row, wanted, '[data-peanut-step="' + step + '"]');
        });
      });

      if (plus) plus.disabled = (parseInt(input.value, 10) || 0) >= max;

      input.addEventListener('change', function () {
        // Guard against a storm from arrow key nudges that land on the value
        // the line already had.
        if (input.value === previous) return;
        if (busy()) {
          input.value = previous;
          return;
        }

        var wanted = parseInt(input.value, 10);
        if (!(wanted >= 0)) wanted = 0;
        if (wanted > max) wanted = max;
        input.value = wanted;
        previous = String(wanted);
        change(row, wanted, '[data-peanut-qty]');
      });

      // Remove is a real link, so it works with scripts off. With them on it
      // takes the same in place path as a quantity of zero.
      var remove = row.querySelector('[data-peanut-remove]');
      if (remove) {
        remove.addEventListener('click', function (event) {
          if (!sectionId || !window.fetch || busy()) return;
          event.preventDefault();
          change(row, 0, '[data-peanut-remove]', function () {
            window.location.href = remove.href;
          });
        });
      }
    });
  }

  document.querySelectorAll('[data-peanut-cart]').forEach(init);
})();
